/**
 * Site chrome: menus, footer links, document title suffix.
 */
import { buildCollection } from "@firecms/core";
import { editorOrAdminPermissions } from "../permissions";
import { CONTENT_STATUS_ENUM } from "../lib/contentStatus";
import {
  STATUS_FIELD_DESCRIPTION,
  publishedSnapshotProperties,
} from "../lib/publishedSnapshotProperties";
import { collectSiteChromeCompletenessIssues } from "../lib/completenessValidation";
import { localizedStringProperties } from "../lib/localeMaps";
import { createStatusPreSave } from "../lib/statusPreSave";
import { createSiteChromeTranslateActions } from "../lib/siteContentTranslateActions";

const navItemProperties = {
  key: {
    name: "Key",
    dataType: "string" as const,
    description: "Stable id for active-state matching (e.g. home, explore, stories)",
  },
  href: {
    name: "URL",
    dataType: "string" as const,
    description:
      "Canonical hub path without language prefix (e.g. /, /destination/, /guides/). Live site localizes by page language.",
    validation: { required: true },
  },
  label: {
    name: "Label",
    dataType: "map" as const,
    properties: localizedStringProperties(),
  },
};

export const siteChromeCollection = buildCollection({
  id: "site_chrome",
  name: "Site Chrome",
  singularName: "Site Chrome",
  path: "site_chrome",
  icon: "Web",
  group: "Site",
  permissions: editorOrAdminPermissions,
  entityActions: createSiteChromeTranslateActions(),
  callbacks: {
    onPreSave: createStatusPreSave({
      collectCompleteness: collectSiteChromeCompletenessIssues,
      entityLabel: "site chrome",
    }),
  },
  properties: {
    name: {
      name: "Name",
      dataType: "string",
      description: "Internal label (e.g. Main)",
      validation: { required: true },
    },
    status: {
      name: "Status",
      dataType: "string",
      enumValues: CONTENT_STATUS_ENUM,
      description: STATUS_FIELD_DESCRIPTION,
    },
    document_title_suffix: {
      name: "Document title suffix",
      dataType: "map",
      description: "Appended after page title (e.g. Venttra)",
      properties: localizedStringProperties(),
    },
    footer_tagline: {
      name: "Footer brand line",
      dataType: "map",
      description: "Shown under the brand in the footer",
      properties: localizedStringProperties(),
    },
    main_nav: {
      name: "Main navigation",
      dataType: "array",
      of: { dataType: "map", properties: navItemProperties },
    },
    drawer_nav: {
      name: "Drawer extra links",
      dataType: "array",
      description: "Additional links in the mobile drawer (below main nav)",
      of: { dataType: "map", properties: navItemProperties },
    },
    footer_nav: {
      name: "Footer navigation",
      dataType: "array",
      of: { dataType: "map", properties: navItemProperties },
    },
    footer_company: {
      name: "Footer company links",
      dataType: "array",
      of: { dataType: "map", properties: navItemProperties },
    },
    ...publishedSnapshotProperties,
    created_at: {
      name: "Created At",
      dataType: "date",
      autoValue: "on_create",
    },
    updated_at: {
      name: "Updated At",
      dataType: "date",
      autoValue: "on_update",
    },
  },
});
